import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TablePagination, TextField, Button, Box, Alert
} from '@mui/material';
import {
  fetchPermits,
  exportPermitsCsv,
} from './api/govopsPermitService';
import { formatGovOpsError } from './api/govopsErrors';
import { downloadCsv } from './api/govopsOperationsService';
import { formatStatus, formatPaymentStatus } from './constants/workflowLabels';
import { getPermitMapPosition } from './utils/mapCoordinates';
import { printPermitNotice } from './utils/pdfExport';
import PageShell from './components/PageShell';

const AdminPermitTable = () => {
  const [permits, setPermits] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const loadPermits = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchPermits();
      setPermits(data);
      setFiltered(data);
    } catch (err) {
      setError(formatGovOpsError(err));
      setPermits([]);
      setFiltered([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPermits();
  }, []);

  useEffect(() => {
    const lower = search.toLowerCase();
    const next = permits.filter(
      (permit) =>
        permit.type?.toLowerCase().includes(lower) ||
        permit.description?.toLowerCase().includes(lower) ||
        permit.id?.toLowerCase().includes(lower) ||
        permit.paymentStatus?.toLowerCase().includes(lower)
    );
    setFiltered(next);
    setPage(0);
  }, [search, permits]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleExportCsv = async () => {
    setExporting(true);
    setError('');
    try {
      const csv = await exportPermitsCsv();
      downloadCsv('robinhoodspace-permits.csv', csv);
    } catch (err) {
      setError(formatGovOpsError(err));
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (permit) => {
    if (permit.date?.seconds) return new Date(permit.date.seconds * 1000).toLocaleString();
    if (permit.createdAt?.seconds) return new Date(permit.createdAt.seconds * 1000).toLocaleString();
    return '—';
  };

  return (
    <PageShell title="Manage permits" subtitle="Search and export permits from GovOps.">
    <Paper sx={{ width: '100%', overflow: 'hidden', p: 2, mt: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <TextField
          fullWidth
          label="Search permits..."
          variant="outlined"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flexGrow: 1, mr: 2 }}
        />
        <Button variant="contained" onClick={handleExportCsv} disabled={exporting || loading}>
          {exporting ? 'Exporting…' : 'Export CSV (GovOps)'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <p>Loading permits from GovOps…</p>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Permit ID</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Payment</TableCell>
                  <TableCell>Balance</TableCell>
                  <TableCell>On map</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((permit) => {
                  const pos = getPermitMapPosition(permit);
                  return (
                  <TableRow key={permit.id}>
                    <TableCell>{permit.id || '—'}</TableCell>
                    <TableCell>{permit.type || '—'}</TableCell>
                    <TableCell>{permit.description || '—'}</TableCell>
                    <TableCell>{formatStatus(permit.status)}</TableCell>
                    <TableCell>{formatPaymentStatus(permit.paymentStatus)}</TableCell>
                    <TableCell>
                      {permit.balanceDue != null
                        ? `$${permit.balanceDue}`
                        : permit.feeAmount != null
                          ? `$${permit.feeAmount}`
                          : '—'}
                    </TableCell>
                    <TableCell>{pos ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{formatDate(permit)}</TableCell>
                    <TableCell>
                      <Link to={`/permit-details/${permit.id}`}>Edit</Link>
                      {' · '}
                      <Link to="/map" state={{ permitId: permit.id }}>Map</Link>
                      {' · '}
                      <button
                        type="button"
                        className="rhs-link-btn"
                        onClick={() => printPermitNotice(permit)}
                      >
                        PDF
                      </button>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </>
      )}
    </Paper>
    </PageShell>
  );
};

export default AdminPermitTable;
